import React, { useState, useEffect } from 'react';
import { processImage, processWithNLP } from '../services/visionService.js';
import { resizeImage } from '../utils.js';


const BulkProcessing = ({ files, description, onCancel, onComplete, onProgress }) => {
  const [currentFile, setCurrentFile] = useState(0);
  const [results, setResults] = useState([]);
  const [errors, setErrors] = useState([]);
  
// In the useEffect of BulkProcessing.js
useEffect(() => {
    let mounted = true;
    const processFiles = async () => {
      const processedResults = [];
      let processedCount = 0;
      
      for (let i = 0; i < files.length; i++) {
        if (!mounted) return;
        
        setCurrentFile(i);
        const file = files[i];
        
        try {
          // Read the file
          const imageData = await readFileAsDataURL(file);
          
          // Resize the image before processing
          const resizedImage = await resizeImage(imageData);
          
          // Process with Vision API
          const recognizedText = await processImage(resizedImage);
          
          if (recognizedText && recognizedText.trim()) {
            // Process with NLP
            const processedText = await processWithNLP(recognizedText, description);
            
            processedResults.push({
              filename: file.name,
              success: Boolean(processedText && processedText.trim()),
              text: processedText
            });
          } else {
            processedResults.push({
              filename: file.name,
              success: false,
              error: 'No text detected'
            });
          }
        } catch (error) {
          console.error(`Error processing ${file.name}:`, error);
          processedResults.push({
            filename: file.name,
            success: false,
            error: error.message
          });
        }
        
        processedCount++;
        const progress = Math.round((processedCount / files.length) * 100);
        onProgress(progress);
      }
      
      if (mounted) {
        setResults(processedResults);
        
        // Filter out errors
        const processingErrors = processedResults.filter(r => !r.success);
        setErrors(processingErrors);
        
        // Complete the process
        onComplete(processedResults);
      }
    };
    
    processFiles();
    
    return () => {
      mounted = false;
    };
  }, [files, description, onComplete, onProgress]);
  
  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  return (
    <div className="mt-6 max-w-4xl mx-auto px-3 sm:px-0">
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">
          Bulk Processing: {description}
        </h2>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-200"
        >
          Cancel
        </button>
      </div>
      
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
          <div 
            className="bg-google-blue h-2.5 rounded-full transition-all duration-300" 
            style={{ width: `${(currentFile + 1) / files.length * 100}%` }}
          ></div>
        </div>
        <div className="text-center text-gray-600">
          Processing {currentFile + 1} of {files.length} images
        </div>
      </div>
      
      {errors.length > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 rounded-md border border-yellow-200">
          <h3 className="font-semibold text-yellow-800 mb-2">Processing Warnings</h3>
          <ul className="text-sm text-yellow-700 list-disc pl-5">
            {errors.slice(0, 5).map((error, index) => (
              <li key={index}>
                {error.filename}: {error.error || 'No pattern matched'}
              </li>
            ))}
            {errors.length > 5 && (
              <li>And {errors.length - 5} more...</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default BulkProcessing;